import DOMPurify from "dompurify";
import {
  InfoCard,
  InputWysiwyg,
  SidePane,
  Form,
  useFormConfig,
  Button,
  FormField,
  FormGrid,
  FormRow,
  displayErrorToast,
  displaySucessToast,
} from "@endya-conformite/ui-kit";
import Loader from "@endya-conformite/ui-kit/src/assets/loader.svg?react";
import { zod } from "@endya-conformite/ui-kit/src/lya/exports";
import { ControleQuinquennalApi } from "@src/api/controleQuinquennal.api";
import { handleGatewayError } from "@src/api/utils/handleGatewayError";
import { replaceNBSP } from "@src/helper/clean.helper";
import { format, parseISO } from "date-fns";
import * as locale from "date-fns/locale";
import { useCallback, useEffect, useState } from "react";
import styles from "./CommentSidePane.module.scss";

type Comment = {
  id: string;
  content: string;
  createdAt: string;
};

const schema = zod.object({
  content: zod
    .string()
    .refine(
      (val) => val.replace(/<[^>]*>/g, "").trim().length > 0,
      "Veuillez saisir un commentaire"
    ),
});

export type CommentFormValues = zod.infer<typeof schema>;

type CommentSidePaneProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  ticketId: string;
  axeLabel: string;
};

function CommentItem({ comment }: { comment: Comment }) {
  // Extract the axe name from the content format: "[Nom de l'axe] content"
  const contentMatch = comment.content.match(/^\[([^\]]+)]\s*(.*)/s);
  const actualContent = contentMatch?.[2] || comment.content;
  const withoutNBSP = replaceNBSP(actualContent).replace(
    "⚠️ NE PAS MODIFIER - COMMENTAIRE ADHERENT",
    ""
  );

  return (
    <FormRow className={styles.commentItem}>
      <FormField>
        <span className={styles.date}>
          {format(parseISO(comment.createdAt), "dd MMMM yyyy 'à' HH:mm", {
            locale: locale.fr,
          })}
        </span>
        <div
          className={styles.commentContent}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(withoutNBSP) }}
        />
      </FormField>
    </FormRow>
  );
}

export function CommentSidePane({
  isOpen,
  setIsOpen,
  ticketId,
  axeLabel,
}: CommentSidePaneProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);

  const form = useFormConfig({
    schema,
    defaultValues: {
      content: "",
    },
  });

  const loadComments = useCallback(async () => {
    setIsLoadingComments(true);
    try {
      const notes = await ControleQuinquennalApi.getTicketNotes(ticketId);

      // Filter notes for the current axe
      const axeComments = notes
        .filter((note) => note.content.startsWith(`[${axeLabel}]`))
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

      setComments(axeComments);
    } catch (error) {
      handleGatewayError({
        onUnhandled: () => {
          console.error("Erreur lors du chargement des commentaires:", error);
        },
      })(error);
    } finally {
      setIsLoadingComments(false);
    }
  }, [ticketId, axeLabel]);

  useEffect(() => {
    if (isOpen) {
      void loadComments();
    }
  }, [isOpen, loadComments]);

  const handleFormSubmit =
    (closePane: (value: boolean) => void) =>
    async (data: CommentFormValues) => {
      try {
        const noteContent = `[${axeLabel}]
        ⚠️ NE PAS MODIFIER - COMMENTAIRE ADHERENT
        ${data.content}`;
        await ControleQuinquennalApi.createTicketNote(ticketId, noteContent);
        displaySucessToast("Votre commentaire a été envoyé");
        closePane(true);
      } catch (error) {
        handleGatewayError({
          onUnhandled: () => {
            displayErrorToast(
              "Une erreur est survenue lors de l'envoi du commentaire"
            );
          },
        })(error);
      }
    };

  const content = form.watch("content");
  const isContentEmpty =
    !content || content.replace(/<[^>]*>/g, "").trim().length === 0;

  return (
    <SidePane
      title="Mes commentaires à l'équipe vérificateurs"
      isOpen={isOpen}
      setIsOpen={(nextIsOpen) => {
        form.reset();
        setIsOpen(nextIsOpen);
      }}
    >
      {(closePane) => (
        <Form formConfig={form} onSubmit={handleFormSubmit(closePane)}>
          <FormGrid disableHorizontalGutter>
            <FormRow>
              <FormField>
                <InfoCard type="info">
                  Vous pouvez envoyer au vérificateur en charge du contrôle de
                  votre société toutes informations et précisions que vous
                  jugerez utiles. Celui-ci vous recontactera par mail ou
                  téléphone en cas de besoin.
                </InfoCard>
              </FormField>
            </FormRow>

            <FormRow>
              <FormField>
                <InputWysiwyg<CommentFormValues> name="content" />
                <div className={styles.actions}>
                  <Button
                    type="submit"
                    disabled={form.formState.isSubmitting || isContentEmpty}
                  >
                    Envoyer le commentaire
                  </Button>
                </div>
              </FormField>
            </FormRow>
            <FormRow>
              <FormField>
                <h2>
                  Historique {comments.length ? `(${comments.length})` : ""}
                </h2>
              </FormField>
            </FormRow>
            {comments.length === 0 && (
              <FormRow>
                <FormField>
                  <InfoCard type="neutral" className={styles.infoCard}>
                    Vous n&apos;avez pas encore envoyé de commentaires.
                  </InfoCard>
                </FormField>
              </FormRow>
            )}
            {comments.length > 0 && (
              <FormRow>
                <FormField>
                  {isLoadingComments && (
                    <div className={styles.loader}>
                      <Loader />
                    </div>
                  )}

                  {!isLoadingComments && comments.length > 0 && (
                    <div className={styles.commentsList}>
                      {comments.map((comment) => (
                        <CommentItem key={comment.id} comment={comment} />
                      ))}
                    </div>
                  )}
                </FormField>
              </FormRow>
            )}
          </FormGrid>
        </Form>
      )}
    </SidePane>
  );
}
